import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import { Divider, Stack, Grid, Box, Button } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { setSnackBar } from 'state/ui'
import { useCreatePaymentMutation } from 'services/payments'
import { useCreateAccountMutation, useCreateAccountTransferMutation } from 'services/accounts'
import { formatSettingsBladeData } from 'components/layouts/settings/cashDrawerUtils'
import { v4 as uuidv4 } from 'uuid'
import DateTimePicker from '../../generic/form/DateTimePicker.jsx'
import dayjs from 'dayjs'

const SettingsBladeModal = ({ onFormStateChange, onHideModal }) => {
  const { bladeTitle, bladeComponents, rowType, bladeActionOnSuccess, fieldsToWatch } = useSelector(
    state => state.ui.formModalProps,
  )
  const methods = useForm()
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const dispatch = useDispatch()
  const { formState, getValues } = methods
  const { isDirty, errors } = formState
  const [createPayment, { isLoading: isLoadingCreatePayment }] = useCreatePaymentMutation()
  const [createAccount, { isLoading: isLoadingCreateAccount }] = useCreateAccountMutation()
  const [createAccountTransfer, { isLoading: isLoadingCreateAccountTransfer }] = useCreateAccountTransferMutation()
  const isLoading = isLoadingCreatePayment || isLoadingCreateAccount || isLoadingCreateAccountTransfer
  const idempotencyKey = useRef(uuidv4())
  const isTransfer = getValues('activityType') === 'transfer'

  fieldsToWatch.forEach(field => {
    methods.watch(field)
  })

  const getAction = action => {
    switch (action) {
      case 'addActivity': {
        if (isTransfer) {
          return createAccountTransfer
        }
        return createPayment
      }
      case 'addAccount': {
        return createAccount
      }
      default:
        return null
    }
  }

  const onSubmit = async data => {
    const action = getAction(bladeActionOnSuccess)
    const dataFormatted = formatSettingsBladeData(bladeActionOnSuccess, {
      ...data,
      idempotencyKey: idempotencyKey.current,
    })
    if (!action) return null

    await action(dataFormatted)
      .unwrap()
      .then(() => {
        dispatch(
          setSnackBar({
            snackbarType: 'success',
            snackbarMessage: `${rowType} successfully added.`,
          }),
        )
        onFormStateChange(true)
        onHideModal()
      })
      .catch(error => {
        dispatch(
          setSnackBar({
            snackbarType: 'error',
            snackbarMessage: error?.data?.error || `An error occurred adding the ${rowType.toLowerCase()}.`,
          }),
        )
      })
    return null
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const renderBladeComponents = componentData => {
    switch (componentData.componentType) {
      case 'dynamic':
        if (componentData.conditions.every(condition => getValues(condition.key) === condition.value)) {
          return renderBladeComponents(componentData.conditionalComponent)
        }
        return renderBladeComponents(componentData.defaultComponent)
      case 'input':
        return (
          <Grid key={componentData.path} mt={2} md={componentData.md || 6}>
            <Input
              defaultValue={componentData.defaultValue || ''}
              disabled={componentData.disabled}
              name={componentData.path}
              label={componentData.label}
              control={methods?.control}
              required={componentData.required}
              InputProps={
                componentData.isAmount
                  ? {
                      inputComponent: InputFormat,
                      inputProps: {
                        inputType: 'price',
                        allowNegative: false,
                        decimalScale: 2,
                      },
                    }
                  : null
              }
              error={!!errors[componentData.path]}
              helperText={errors[componentData.path] ? 'This field is required' : ''}
            />
          </Grid>
        )
      case 'select':
        return (
          <Grid key={componentData.path} mt={2} md={componentData.md || 6}>
            <Select
              label={componentData.label}
              name={componentData.path}
              defaultValue={componentData.defaultValue || componentData.items[0]?.id}
              control={methods?.control}
              items={componentData.items}
              disabled={componentData.disabled || false}
            />
          </Grid>
        )
      case 'doubleSelect':
        return (
          <Grid key={componentData.path} mt={2} md={componentData.md || 6}>
            <Stack direction="row" spacing={2}>
              <Select
                label={componentData.label1}
                name={componentData.path1}
                defaultValue={componentData.defaultValue || componentData.items[0]?.id}
                control={methods?.control}
                items={componentData.items}
                disabled={componentData.disabled || false}
              />
              <Select
                label={componentData.label2}
                name={componentData.path2}
                defaultValue={
                  !componentData.defaultValue || componentData.items[0]?.id === componentData.defaultValue
                    ? componentData.items[1]?.id
                    : componentData.items[0]?.id
                }
                control={methods?.control}
                items={componentData.items}
                disabled={componentData.disabled || false}
              />
            </Stack>
          </Grid>
        )
      case 'date-input':
        return (
          <Grid key={componentData.path} mt={2} md={componentData.md || 6}>
            <Stack direction="row" spacing={2}>
              <DateTimePicker
                label={componentData.label}
                name={componentData.path}
                control={methods?.control}
                variant="outlined"
                defaultDateTime={dayjs()} // Default to the yard's timezone
                dateTimePickerProps={{
                  disabled: false,
                  size: 'small',
                  'data-test': componentData.dataTag,
                  timezone: defaultYardTimezone,
                  maxDate: dayjs(),
                }}
                error={!!errors?.[componentData.path]}
                helperText={errors?.[componentData.path]?.message}
              />
            </Stack>
          </Grid>
        )
      default:
        return null
    }
  }

  return (
    <form onSubmit={methods?.handleSubmit(onSubmit)}>
      <Stack height="100vh">
        <FormModalHeading title={bladeTitle || ''} backButtonPosition="right" onBack={() => onHideModal()} />
        <Divider />
        <Box flexGrow={1}>
          <Grid container m={4} direction="column">
            {bladeComponents ? bladeComponents?.map(renderBladeComponents) : null}
          </Grid>
        </Box>
        <Divider />
        <Stack direction="row" p={2} spacing={2}>
          <LoadingButton type="submit" variant="contained" disabled={!isDirty} loading={isLoading}>
            {`Add ${rowType || ''}`}
          </LoadingButton>
          <Button onClick={onHideModal}>Cancel</Button>
        </Stack>
      </Stack>
    </form>
  )
}

SettingsBladeModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

SettingsBladeModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default SettingsBladeModal
